import { useState } from 'react';
import { cn } from '../../utils/cn';
import { ImageSkeleton } from './SkeletonLoader';

interface EnhancedImageProps {
  src: string;
  alt: string;
  className?: string;
  overlayOnHover?: boolean;
  zoomOnHover?: boolean;
  overlayContent?: React.ReactNode;
}

export const EnhancedImage = ({
  src,
  alt,
  className,
  overlayOnHover = false,
  zoomOnHover = true,
  overlayContent,
}: EnhancedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  return (
    <div className={cn('relative overflow-hidden group', className)}>
      {/* Skeleton loader while loading */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0">
          <ImageSkeleton />
        </div>
      )}

      {/* Image */}
      {!hasError && (
        <img
          src={src}
          alt={alt}
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
          className={cn(
            'w-full h-full object-cover transition-all duration-700',
            zoomOnHover && 'group-hover:scale-110',
            !isLoaded && 'opacity-0'
          )}
        />
      )}

      {/* Error fallback */}
      {hasError && (
        <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
          <span className="text-gray-400 text-sm">Failed to load image</span>
        </div>
      )}

      {/* Overlay on hover */}
      {overlayOnHover && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {overlayContent && (
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
              {overlayContent}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
